import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  Container, 
  Paper, 
  Tabs, 
  Tab, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Divider, 
  Alert, 
  Snackbar, 
  CircularProgress,
  FormControlLabel,
  Checkbox,
  IconButton
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Cancel as CancelIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Create an Axios instance with default configuration
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '', // Use environment variable or empty string
});

// Add a request interceptor to include the auth token in all requests
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function ProfilePage() {
  const { currentUser, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Addresses tab data
  const [addresses, setAddresses] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressFormData, setAddressFormData] = useState({
    address_type: '',
    street_address: '',
    apartment_suite_etc: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    is_default: false
  });
  const [addressFormErrors, setAddressFormErrors] = useState({});
  
  // Active tab state
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (currentUser) {
      setFormData({
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        email: currentUser.email || ''
      });
    }
  }, [currentUser]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchAddresses();
    }
  }, [activeTab]);

  const fetchAddresses = async () => {
    setIsLoadingAddresses(true);
    try {
      const response = await API.get('/api/addresses');
      setAddresses(response.data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setNotification({
        type: 'error',
        message: 'Failed to load addresses. Please try again.'
      });
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  const validateProfileForm = () => {
    const errors = {};
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email address is invalid';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setNotification(null);
    
    try {
      await updateProfile(formData);
      setNotification({
        type: 'success',
        message: 'Profile updated successfully!'
      });
    } catch (error) {
      console.error('Profile update failed:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    setAddressFormData(prevData => ({
      ...prevData,
      [name]: val
    }));
    
    if (addressFormErrors[name]) {
      setAddressFormErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  const validateAddressForm = () => {
    const errors = {};
    
    if (!addressFormData.address_type.trim()) {
      errors.address_type = 'Address type is required';
    }
    if (!addressFormData.street_address.trim()) {
      errors.street_address = 'Street address is required';
    }
    if (!addressFormData.city.trim()) {
      errors.city = 'City is required';
    }
    if (!addressFormData.state_province.trim()) {
      errors.state_province = 'State/Province is required';
    }
    if (!addressFormData.postal_code.trim()) {
      errors.postal_code = 'Postal code is required';
    }
    if (!addressFormData.country.trim()) {
      errors.country = 'Country is required';
    }
    
    setAddressFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAddressForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setNotification(null);
    
    try {
      if (selectedAddress) {
        // Update existing address
        await API.put(`/api/addresses/${selectedAddress.address_id}`, addressFormData);
        setNotification({
          type: 'success',
          message: 'Address updated successfully!'
        });
      } else {
        // Create new address
        await API.post('/api/addresses', addressFormData);
        setNotification({
          type: 'success',
          message: 'Address added successfully!'
        });
      }
      
      // Reset form and fetch updated addresses
      setAddressFormData({
        address_type: '',
        street_address: '',
        apartment_suite_etc: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: '',
        is_default: false
      });
      setSelectedAddress(null);
      fetchAddresses();
    } catch (error) {
      console.error('Address operation failed:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to save address. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const editAddress = (address) => {
    setSelectedAddress(address);
    setAddressFormData({
      address_type: address.address_type,
      street_address: address.street_address,
      apartment_suite_etc: address.apartment_suite_etc || '',
      city: address.city,
      state_province: address.state_province,
      postal_code: address.postal_code,
      country: address.country,
      is_default: address.is_default
    });
  };

  const deleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }
    
    try {
      await API.delete(`/api/addresses/${addressId}`);
      setNotification({
        type: 'success',
        message: 'Address deleted successfully!'
      });
      fetchAddresses();
      
      // Reset form if we were editing the deleted address
      if (selectedAddress && selectedAddress.address_id === addressId) {
        setSelectedAddress(null);
        setAddressFormData({
          address_type: '',
          street_address: '',
          apartment_suite_etc: '',
          city: '',
          state_province: '',
          postal_code: '',
          country: '',
          is_default: false
        });
      }
    } catch (error) {
      console.error('Delete address failed:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to delete address. Please try again.'
      });
    }
  };

  const cancelAddressEdit = () => {
    setSelectedAddress(null);
    setAddressFormData({
      address_type: '',
      street_address: '',
      apartment_suite_etc: '',
      city: '',
      state_province: '',
      postal_code: '',
      country: '',
      is_default: false
    });
    setAddressFormErrors({});
  };

  // Get icon for address type
  const getAddressIcon = (type) => {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('home')) return <HomeIcon />;
    if (lowerType.includes('work') || lowerType.includes('business')) return <BusinessIcon />;
    if (lowerType.includes('shipping') || lowerType.includes('delivery')) return <ShippingIcon />;
    return <HomeIcon />;
  };

  return (
    <Container maxWidth="lg">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            My Profile
          </Typography>
        </Box>

        <Snackbar
          open={notification !== null}
          autoHideDuration={6000}
          onClose={() => setNotification(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setNotification(null)} 
            severity={notification?.type || 'info'} 
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification?.message}
          </Alert>
        </Snackbar>
        
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              aria-label="profile tabs"
              sx={{ px: 2 }}
            >
              <Tab label="User Information" />
              <Tab label="Addresses" />
            </Tabs>
          </Box>
          
          {/* User Information Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <form onSubmit={handleProfileSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Username"
                      name="username"
                      value={currentUser?.username || ''}
                      disabled
                      fullWidth
                      variant="outlined"
                      helperText="Username cannot be changed"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleProfileChange}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                      required
                      fullWidth
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="First Name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleProfileChange}
                      error={!!formErrors.first_name}
                      helperText={formErrors.first_name}
                      fullWidth
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Last Name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleProfileChange}
                      error={!!formErrors.last_name}
                      helperText={formErrors.last_name}
                      fullWidth
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          )}
          
          {/* Addresses Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              {isLoadingAddresses ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" component="h2" gutterBottom>
                      My Addresses
                    </Typography>
                    
                    {addresses.length === 0 ? (
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 3, 
                          textAlign: 'center',
                          backgroundColor: 'background.default',
                          borderRadius: 2,
                          border: '1px dashed',
                          borderColor: 'divider'
                        }}
                      >
                        <Typography variant="body1" color="text.secondary">
                          You have no saved addresses. Use the form to add your first address.
                        </Typography>
                      </Paper>
                    ) : (
                      <Box sx={{ mt: 2 }}>
                        {addresses.map(address => (
                          <Card 
                            key={address.address_id} 
                            sx={{ 
                              mb: 2, 
                              borderRadius: 2,
                              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.1)',
                              }
                            }}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ mr: 1, color: 'primary.main' }}>
                                  {getAddressIcon(address.address_type)}
                                </Box>
                                <Typography variant="h6" component="h3">
                                  {address.address_type}
                                </Typography>
                                {address.is_default && (
                                  <Box 
                                    component="span" 
                                    sx={{ 
                                      ml: 1,
                                      px: 1, 
                                      py: 0.5, 
                                      bgcolor: 'primary.light', 
                                      color: 'primary.contrastText',
                                      borderRadius: 1,
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    Default
                                  </Box>
                                )}
                              </Box>
                              
                              <Divider sx={{ my: 1 }} />
                              
                              <Typography variant="body1">
                                {address.street_address}
                                {address.apartment_suite_etc && `, ${address.apartment_suite_etc}`}
                              </Typography>
                              <Typography variant="body1">
                                {address.city}, {address.state_province} {address.postal_code}
                              </Typography>
                              <Typography variant="body1">
                                {address.country}
                              </Typography>
                            </CardContent>
                            
                            <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => editAddress(address)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => deleteAddress(address.address_id)}
                              >
                                Delete
                              </Button>
                            </CardActions>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 3, 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="h6" component="h2" gutterBottom>
                        {selectedAddress ? 'Edit Address' : 'Add New Address'}
                      </Typography>
                      
                      <form onSubmit={handleAddressSubmit}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label="Address Type"
                              name="address_type"
                              value={addressFormData.address_type}
                              onChange={handleAddressChange}
                              error={!!addressFormErrors.address_type}
                              helperText={addressFormErrors.address_type || "e.g., Home, Work, etc."}
                              required
                              fullWidth
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              label="Street Address"
                              name="street_address"
                              value={addressFormData.street_address}
                              onChange={handleAddressChange}
                              error={!!addressFormErrors.street_address}
                              helperText={addressFormErrors.street_address}
                              required
                              fullWidth
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              label="Apartment, Suite, etc. (optional)"
                              name="apartment_suite_etc"
                              value={addressFormData.apartment_suite_etc}
                              onChange={handleAddressChange}
                              error={!!addressFormErrors.apartment_suite_etc}
                              helperText={addressFormErrors.apartment_suite_etc}
                              fullWidth
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="City"
                              name="city"
                              value={addressFormData.city}
                              onChange={handleAddressChange}
                              error={!!addressFormErrors.city}
                              helperText={addressFormErrors.city}
                              required
                              fullWidth
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="State/Province"
                              name="state_province"
                              value={addressFormData.state_province}
                              onChange={handleAddressChange}
                              error={!!addressFormErrors.state_province}
                              helperText={addressFormErrors.state_province}
                              required
                              fullWidth
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Postal Code"
                              name="postal_code"
                              value={addressFormData.postal_code}
                              onChange={handleAddressChange}
                              error={!!addressFormErrors.postal_code}
                              helperText={addressFormErrors.postal_code}
                              required
                              fullWidth
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Country"
                              name="country"
                              value={addressFormData.country}
                              onChange={handleAddressChange}
                              error={!!addressFormErrors.country}
                              helperText={addressFormErrors.country}
                              required
                              fullWidth
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="is_default"
                                  checked={addressFormData.is_default}
                                  onChange={handleAddressChange}
                                  color="primary"
                                />
                              }
                              label="Set as default address"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                              {selectedAddress && (
                                <Button
                                  type="button"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<CancelIcon />}
                                  onClick={cancelAddressEdit}
                                >
                                  Cancel
                                </Button>
                              )}
                              
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                disabled={isSubmitting}
                              >
                                {isSubmitting 
                                  ? (selectedAddress ? 'Updating...' : 'Adding...') 
                                  : (selectedAddress ? 'Update Address' : 'Add Address')}
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </form>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>
    </Container>
  );
}

export default ProfilePage;